import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { Button, Divider, List, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import propTypes from 'prop-types';
import { SUMA } from '../../../constants/organizations';
import { useSelector } from 'react-redux';
import Message from './Message';
import { useFirebase } from 'react-redux-firebase';

const useStyles = (small, openHeader) => {
  return makeStyles((theme) => ({
    messageArea: {
      display: 'flex',
      flexDirection: 'column',
      flex: small && 1,
      paddingRight: 20,
      width: '100%',
      paddingLeft: 20,
      paddingBottom: 16,
      marginTop: openHeader ? 140 : 50,
      // maxHeight: !small && (openHeader ? 'calc(100vh - 368px)' : 'calc(100vh - 228px)'),
      height: '100%',
      maxHeight: `calc(100% - ${openHeader ? 212 : 122}px)`,
      overflowY: 'auto',
      bottom: '0',
      background: '#dfdfdf',
      '&::-webkit-scrollbar': {
        width: '1em',
      },
      '&::-webkit-scrollbar-track': {
        '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0,0,0,.2)',
      },
    },
    todayRef: {
      backgroundColor: '#c9beb3',
      opacity: '.8',
      borderRadius: 8,
      padding: '4px 16px',
      fontSize: 8,
    },
    showHistoryContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: 4,
    },
  }));
};

const MessagesList = (props) => {
  const {
    chat,
    allMessages,
    showHistory,
    setShowHistory,
    modalOpen,
    oldChat,
    setModalContent,
    setModalOpen,
    openHeader,
  } = props;
  const small = useMediaQuery('(max-width:600px)');
  const classes = useStyles(small, openHeader)();
  const organization = useSelector((state) => state.app.organization);
  const messagesContainerEl = useRef(null);
  const [cacheFiles, setCacheFiles] = useState([]);

  const firebase = useFirebase();
  const ref = firebase.storage().ref();

  useEffect(() => {
    if (oldChat) {
      for (const message of oldChat.messages) {
        if (
          (message.contentType === 'audio' ||
            (message.contentType === 'image' && message.hasDownload)) &&
          !cacheFiles.find((e) => e.id === message.message)
        ) {
          let folder = 'images';
          if (message.contentType === 'audio') {
            folder = 'voice_records';
          }
          ref
            .child(`${folder}/${message.message}`)
            .getDownloadURL()
            .then((res) => {
              setCacheFiles((previousState) => [
                ...previousState,
                { id: message.message, url: res },
              ]);
            });
        }
      }
    }
  }, [oldChat]);

  useEffect(() => {
    for (const message of chat.messages) {
      if (
        (message.contentType === 'audio' ||
          (message.contentType === 'image' && message.hasDownload)) &&
        !cacheFiles.find((e) => e.id === message.message)
      ) {
        let folder = 'images';
        if (message.contentType === 'audio') {
          folder = 'voice_records';
        }
        ref
          .child(`${folder}/${message.message}`)
          .getDownloadURL()
          .then((res) => {
            setCacheFiles((previousState) => [...previousState, { id: message.message, url: res }]);
          });
      }
    }
  }, [chat.messages.length]);

  const generateRef = (date, label) => {
    const now = new Date(Date.now());
    const diffDays = Math.abs(date.getDate() - now.getDate());
    if (diffDays === 0)
      return (
        <Box className={classes.todayRef}>
          <Typography>Hoy</Typography>
        </Box>
      );
    else if (diffDays === 1)
      return (
        <Box className={classes.todayRef} sx={{ marginTop: label === 0 && 2 }}>
          <Typography>Ayer</Typography>
        </Box>
      );
    else
      return (
        <Box sx={{ marginTop: label === 0 ? '16px' : 0 }}>
          <Typography sx={{ color: '#353535', fontSize: 14 }}>
            {moment(date).format('LLLL')}
          </Typography>
        </Box>
      );
  };

  const messages =
    chat &&
    allMessages.map((messageItem, index) => {
      if (messageItem.isDivider)
        return (
          <Box
            key={index}
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
            my={2}
          >
            {generateRef(messageItem.lastDate, 0)}
            <Divider className={classes.divider} />
            {generateRef(messageItem.messageDate, 1)}
          </Box>
        );

      return (
        <Message
          key={index}
          chat={chat}
          type="text"
          message={messageItem}
          setModalContent={setModalContent}
          setModalOpen={setModalOpen}
          cacheFiles={cacheFiles}
        />
      );
    });

  // scroll down if messages changed
  useEffect(() => {
    // avoid side effect when an image is shown in modal
    if (!messages || modalOpen) return;

    const scrollingElement = messagesContainerEl.current;
    scrollingElement.scrollTop = scrollingElement.scrollHeight;
  }, [messages.length, modalOpen]);

  const isSuma = SUMA.includes(organization.id);

  return (
    <List className={classes.messageArea} ref={messagesContainerEl}>
      {isSuma && !showHistory && (
        <Box className={classes.showHistoryContainer}>
          <Button variant="contained" onClick={() => setShowHistory(true)} color="secondary">
            Mostrar historial
          </Button>
        </Box>
      )}
      {isSuma && showHistory && !oldChat && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
          <Typography sx={{ fontSize: 14 }}>No hay chats antiguos</Typography>
        </Box>
      )}
      {messages}
    </List>
  );
};

const Props = {
  chat: propTypes.object,
  showHistory: propTypes.bool,
  setShowHistory: propTypes.func,
  oldChat: propTypes.object,
  allMessages: propTypes.array,
  setModalContent: propTypes.func,
  modalOpen: propTypes.bool,
  setModalOpen: propTypes.func,
  openHeader: propTypes.bool,
};

MessagesList.propTypes = Props;

export default MessagesList;
